import { isPlatformBrowser } from '@angular/common';
import {
  AfterViewInit,
  Component,
  Inject,
  PLATFORM_ID,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ElementRef,
  ViewChild,
} from '@angular/core';
import { Mappa } from '../types/payload-types';

@Component({
  selector: 'app-map',
  imports: [],
  templateUrl: './map.component.html',
  styleUrl: './map.component.scss',
})
export class MapComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() mappaData?: Mappa;
  @ViewChild('mapContainer', { static: true })
  mapContainer!: ElementRef<HTMLDivElement>;

  private isBrowser: boolean;
  private map: any;
  private L: any;
  private userLocationMarker: any;
  private markersLayer: any;

  // Pending operations to apply when map is ready
  private pendingView: { lat: number; lng: number; zoom: number } | null = null;
  private pendingUserLocation: { lat: number; lng: number } | null = null;

  constructor(@Inject(PLATFORM_ID) platformId: Object) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  ngOnDestroy(): void {
    if (this.map) {
      this.map.remove();
      this.map = null;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Quando mappaData cambia e la mappa è già inizializzata, aggiungi i marker
    if (changes['mappaData'] && this.map && this.L) {
      this.addMarkers();
    }
  }

  async ngAfterViewInit(): Promise<void> {
    if (!this.isBrowser) return;

    // Prevent double initialization
    if (this.map) {
      console.log('Map already initialized, skipping');
      return;
    }

    const leafletModule = await import('leaflet');
    this.L = leafletModule.default || leafletModule;

    // Verify Leaflet loaded correctly
    if (!this.L || !this.L.map) {
      console.error('Leaflet failed to load properly');
      return;
    }

    // Check if map container exists
    const container = this.mapContainer?.nativeElement;
    if (!container) {
      console.error('Map container element not found');
      return;
    }

    // Clear container and remove any Leaflet references
    container.innerHTML = '';
    delete (container as any)._leaflet_id;

    try {
      // Initialize map with default view and zoom controls on the right
      this.map = this.L.map(container, {
        zoomControl: false, // Disable default zoom control
      }).setView([41.9028, 12.4964], 10);
    } catch (error) {
      console.error('Error initializing map:', error);
      return;
    }

    // Add zoom control to the right
    this.L.control
      .zoom({
        position: 'topright',
      })
      .addTo(this.map);

    // Fix for Leaflet icon issue with Angular CLI bundling
    const iconRetinaUrl = '/media/marker-icon-2x.png';
    const iconUrl = '/media/marker-icon.png';
    const shadowUrl = '/media/marker-shadow.png';
    const iconDefault = this.L.icon({
      iconRetinaUrl,
      iconUrl,
      shadowUrl,
      iconSize: [25, 41],
      iconAnchor: [12, 41],
      popupAnchor: [1, -34],
      tooltipAnchor: [16, -28],
      shadowSize: [41, 41],
    });
    this.L.Marker.prototype.options.icon = iconDefault;

    this.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors',
    }).addTo(this.map);

    // Inizializza il layer per i marker
    this.markersLayer = this.L.layerGroup().addTo(this.map);

    // Aggiungi i marker se i dati sono già disponibili
    this.addMarkers();

    // Apply any pending operations
    this.applyPendingOperations();
  }

  private applyPendingOperations(): void {
    if (this.pendingView) {
      this.map.setView(
        [this.pendingView.lat, this.pendingView.lng],
        this.pendingView.zoom,
      );
      this.pendingView = null;
    }

    if (this.pendingUserLocation) {
      this.setUserLocation(
        this.pendingUserLocation.lat,
        this.pendingUserLocation.lng,
      );
      this.pendingUserLocation = null;
    }
  }

  private addMarkers(): void {
    if (!this.map || !this.L || !this.markersLayer) return;

    // Rimuovi i marker esistenti
    this.markersLayer.clearLayers();

    const chiese = this.mappaData?.chiese || [];

    chiese
      .filter(
        (chiesa) =>
          chiesa.coordinate?.lat != null && chiesa.coordinate?.lng != null,
      )
      .forEach((chiesa) => {
        const popupContent = this.createPopupContent(chiesa);
        const marker = this.L.marker([
          chiesa.coordinate.lat,
          chiesa.coordinate.lng,
        ]).bindPopup(popupContent);

        // Store church ID as custom property for later reference
        marker._churchId = chiesa.id;

        this.markersLayer.addLayer(marker);
      });
  }

  private createPopupContent(chiesa: NonNullable<Mappa['chiese']>[number]): HTMLElement {
    const container = document.createElement('div');

    const title = document.createElement('b');
    title.textContent = chiesa.nome;
    container.appendChild(title);

    if (chiesa.indirizzo) {
      const p = document.createElement('p');
      const strong = document.createElement('strong');
      strong.textContent = 'Indirizzo: ';
      p.appendChild(strong);

      const link = document.createElement('a');
      link.href = `https://maps.google.com/maps?q=${encodeURIComponent(chiesa.indirizzo)}`;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.textContent = chiesa.indirizzo;
      p.appendChild(link);

      container.appendChild(p);
    }

    if (chiesa.orari) {
      const p = document.createElement('p');
      const strong = document.createElement('strong');
      strong.textContent = 'Orari: ';
      p.appendChild(strong);
      p.appendChild(document.createTextNode(chiesa.orari));
      container.appendChild(p);
    }

    if (chiesa.telefono) {
      const p = document.createElement('p');
      const strong = document.createElement('strong');
      strong.textContent = 'Tel: ';
      p.appendChild(strong);

      const link = document.createElement('a');
      link.href = `tel:${chiesa.telefono}`;
      link.textContent = chiesa.telefono;
      p.appendChild(link);

      container.appendChild(p);
    }

    return container;
  }

  // Public methods to control map from outside
  public setUserLocation(lat: number, lng: number): void {
    if (!this.map || !this.L) {
      // Save for later when map is ready
      this.pendingUserLocation = { lat, lng };
      return;
    }

    // Remove existing user location marker if present
    if (this.userLocationMarker) {
      this.map.removeLayer(this.userLocationMarker);
    }

    // Add user location marker
    this.userLocationMarker = this.L.circleMarker([lat, lng], {
      radius: 8,
      color: 'blue',
      fillColor: 'blue',
      fillOpacity: 0.8,
    }).addTo(this.map);
  }
  public focusOnChurch(churchId: string): void {
    if (!this.map || !this.mappaData?.chiese || !this.markersLayer) return;

    const church = this.mappaData.chiese.find((c) => c.id === churchId);
    if (church?.coordinate?.lat != null && church?.coordinate?.lng != null) {
      this.map.setView([church.coordinate.lat, church.coordinate.lng], 15);

      // Find and open the popup for this church
      this.markersLayer.eachLayer((layer: any) => {
        if (layer._churchId === churchId && layer.openPopup) {
          layer.openPopup();
        }
      });
    }
  }

  public focusOnCoordinates(lat: number, lng: number, zoom: number = 12): void {
    if (!this.map) {
      // Save for later when map is ready
      this.pendingView = { lat, lng, zoom };
      return;
    }
    this.map.setView([lat, lng], zoom);
  }
}
